import { z } from 'zod'

import { SubscriberIdSchema } from '../subscriber/subscriber.dto'
import { TicketIdSchema } from '../ticket/ticket.dto'

import { TicketMessageIdSchema } from './ticketMessageId'

/**
 * __TicketMessageAttachmentId__
 *
 * The unique ID of a TicketMessageAttachment in our system.
 */
export const TicketMessageAttachmentIdSchema = z
  .string()
  .brand('TicketMessageAttachmentId')
export type TicketMessageAttachmentId = z.infer<
  typeof TicketMessageAttachmentIdSchema
>

/**
 * __RemoteTicketMessageAttachmentId__
 *
 * If the attachment was originally sourced from a third-party service, this is the ID of the message in that service.
 */
export const RemoteTicketMessageAttachmentIdSchema = z
  .string()
  .brand('RemoteTicketMessageAttachmentId')
export type RemoteTicketMessageAttachmentId = z.infer<
  typeof RemoteTicketMessageAttachmentIdSchema
>

/**
 * __Ticket Message Attachment__
 *
 * A TicketMessageAttachment is a file attached to a ticket message.
 */
export const TicketMessageAttachmentSchema = z.object({
  // The unique ID of this single message attachment.
  id: TicketMessageAttachmentIdSchema,
  // If this attachment was originally sourced from a third-party service, this is the ID of the message in that service.
  remoteId: RemoteTicketMessageAttachmentIdSchema.nullish(),
  // The ticket that this attachment belongs to.
  ticketId: TicketIdSchema,
  // The ticket message that this attachment belongs to.
  messageId: TicketMessageIdSchema,
  remoteCreatedAt: z.date().nullish(),
  createdAt: z.date(),
  // URL to access the attachment.
  url: z.string().url(),
  // File name
  name: z.string(),
  size: z
    .number()
    .nullish()
    .transform((val) => val ?? undefined),
  // Mime type
  type: z.string(),
  subscriberId: SubscriberIdSchema,
})

export type TicketMessageAttachment = z.infer<
  typeof TicketMessageAttachmentSchema
>

/**
 * __TicketMessageAttachmentNestedUpsert__
 *
 * Used when transforming a third-party attachment into our schema - prior to create operation.
 */
export const TicketMessageAttachmentNestedUpsertSchema =
  TicketMessageAttachmentSchema.omit({
    id: true,
    ticketId: true,
    createdAt: true,
    messageId: true,
  }).extend({
    id: TicketMessageAttachmentIdSchema.optional(),
  })

export type TicketMessageAttachmentNestedUpsert = z.infer<
  typeof TicketMessageAttachmentNestedUpsertSchema
>

/**
 * __TicketMessageAttachmentUpsert__
 *
 * An upsert operation for TicketMessageAttachment, used for inserting or updating records.
 */
export const TicketMessageAttachmentUpsertSchema =
  TicketMessageAttachmentSchema.omit({
    id: true,
    createdAt: true,
  }).extend({
    id: TicketMessageAttachmentIdSchema.optional(),
  })

export type TicketMessageAttachmentUpsert = z.infer<
  typeof TicketMessageAttachmentUpsertSchema
>
